import h from 'hyperscript';
import { renderLabels } from './accouterment';
import IFramePlayer from 'src/entry_points/iframe_player';
import { DEFAULT_ASPECT_RATIO } from 'configuration/defaults';
import { NATIVE_EVENT_NAMES } from 'constants';
import { EmbedPlayerFactory } from './embed-player-factory';
import { EXTERNAL_EVENT_MAP as apiEvents } from 'api/player/event_maps';

export class CustomElement {
    constructor(dispatcher, attributes = {}) {
        this.displayed = false;
        this.dispatcher = dispatcher;
        this.attributes = attributes;

        const { title, description, textLocation } = attributes;

        this.embed = (
            <div { ...this.rootContainerAttrs }>
                <div style="position: relative; display: block; max-width: 100%;">
                    <div className="player-container hapyak-player-embed" { ...this.playerContainerAttrs }></div>
                </div>
                { renderLabels({ title, description, position: textLocation }) }
            </div>
        );

        this.init();
    }

    init() {
        if (this.src) {
            this.loadPlayer()
                .then(player => {
                    this.player = player;
                    this.dispatcher.addEventListener('play', () => this.embed.setAttribute('data-started', true));
                    this.dispatcher.addEventListener('previewHidden', () => this.embed.setAttribute('data-started', true));
                })
        }
    }

    loadPlayer() {
        return EmbedPlayerFactory.createPlayer({
            iframe: this.usesIFrame,
            ...this.playerArgs
        })
    }

    showPlayer(event) {
        if (!this.player) {
            this.loadPlayer()
                .then(player => {
                    this.player = player;
                    this.showPlayer();
                })
                .catch(this.showError);
            
            return;
        }

        this.embed.setAttribute('data-displayed', true);
        this.displayed = true;
    }

    hidePlayer() {
        this.embed.setAttribute('data-displayed', false);
        this.displayed = false;
    }

    onEmbedClick = event => {
        event.stopPropagation();

        if (this.displayed) {
            this.hidePlayer();
        }
    }

    dispatchEvent(args) {
        if (this.dispatcher) {
            this.dispatcher.dispatchEvent(args);
        }
    }

    get src() {
        return this.attributes.src;
    }

    set src(src) {
        this.attributes.src = src;

        if (!this.player) {
            this.init();
        } else {
            this.player.reload(src);
        }
    }

    // initial player container styles
    get playerContainerAttrs() {
        const { width, height } = this.attributes,
            { fixedWidth, fixedHeight } = this.fixedDimensions,
            style = {};

        if (fixedWidth) {
            style.width = width;
        }

        if (fixedHeight) {
            style.height = height;
        }

        if (fixedWidth && !fixedHeight) {
            if (width.endsWith('px')) {
                let proportionalHeight = Number(width.replace('px','')) * DEFAULT_ASPECT_RATIO;
                style.height = `${proportionalHeight}px`; 
            } else if (width.endsWith('%')) {
                let proportionalHeight = Number(width.replace('%','')) * DEFAULT_ASPECT_RATIO;
                style['padding-top'] = `${proportionalHeight}%`; 
            }
        }

        if (!fixedWidth && !fixedHeight && this.usesIFrame) {
            style['padding-top'] = `${DEFAULT_ASPECT_RATIO * 100}%`;
        }

        return {
            style,
            ['data-fixed-width']: fixedWidth,
            ['data-fixed-height']: fixedHeight
        };
    }

    get playerArgs() {
        const args = {
            container: this.playerContainer,
            overridesUrl: this.src,
            ...this.apiEventProxies
        };

        Object.keys(this.attributes).forEach(key => {
            if (this.attributes[key] || this.attributes[key] === false) {
                if (!args[key]) { // exclude on<event> listeners. are handled below in `get apiEventProxies`
                    args[key] = this.attributes[key];
                }
            }
        });

        return args;
    }

    get rootContainerAttrs() {
        const { fixedWidth, fixedHeight } = this.fixedDimensions,
            style = {};

        if (fixedWidth || fixedHeight) {
            style.overflow = 'hidden';
        }

        if (fixedWidth) {
            style.width = fixedWidth;
        }

        if (fixedHeight) {
            style.height = fixedHeight;
        }

        return {
            style,
            className: 'hapyak-embed',
            onclick: this.onEmbedClick,
            ['data-fixed-width']: fixedWidth,
            ['data-fixed-height']: fixedHeight,
            ['data-displayed']: 'true',
            ['data-presentation']: this.presentation
        }
    }

    get hasFixedDimensions() {
        const { width, height } = this.attributes;
        return (width && width !== 'auto') || (height && height !== 'auto');
    }

    get fixedDimensions() {
        const { width, height } = this.attributes;
        let fixedWidth = false, fixedHeight = false;

        if (width && width !== 'auto' && (width.endsWith('px') || width.endsWith('%'))) {
            fixedWidth = width;
        }

        if (height && height !== 'auto' && height.endsWith('px')) {
            fixedHeight = height;
        }

        return { fixedWidth, fixedHeight }
    }

    get apiEventProxies() {
        return (
            Object
                .keys(apiEvents)
                .reduce((handlers, eventName) => {
                    const callback = `on${eventName}`;
                    
                    handlers[callback] = (detail = {}) => {
                        const event = new CustomEvent(eventName, { detail });
                            
                        this.dispatchEvent(event);

                        // "native" inline event callbacks ('onplay', 'ontimeupdate', etc) will be trigged by 'dispatch' call above
                        // other events ('onanalytics') need to be called manually
                        if (this.attributes[callback] && typeof this.attributes[callback] === 'function' && !NATIVE_EVENT_NAMES.includes(callback)) {
                            this.attributes[callback](event);
                        }
                    };

                    return handlers;
                }, {})
        )
    }

    get presentation() {
        return ''; // intended to be overridden by subclass
    }

    get playerContainer() {
        return this.embed.querySelector('.player-container');
    }

    get usesIFrame() {
        const { iframe } = this.attributes;
        return (iframe === 'false' || iframe === false) ? false : true;
    }
}